// Shared with mju-aioe/packages/shared/src/schemas/secrets.ts — keep the two identical.
/**
 * Secrets, end to end: the API keys, tokens and connection strings agents'
 * tools need, kept by name at a **home** and released to the workbenches and
 * nodes that may use them — never committed to a repository, never written
 * to disk by a workbench host, and never shown to an agent unless the secret
 * says so.
 *
 * The home is the same in every edition as for the rest of a project's
 * records: the organisation (AIOE) for a project registered with it (every
 * one is the organisation's from AIOE 0.9.0), the team's hub for one shared
 * with the team, the desktop's (or node's) private hub for one kept on this
 * computer (the Personal space). All three serve these routes with these shapes:
 *
 *   GET    /secrets/v1?projectId=&personal=1   { secrets: SecretMeta[], manage: SecretScope[] } (`personal=1`: the caller's own are listed too)
 *                                          what applies to the project (every scope), names and facts — never a value;
 *                                          `manage` lists the scopes the caller may add to or change
 *   POST   /secrets/v1                     a draft with its value → 201 the SecretMeta (version 1); 409 name_taken when
 *                                          that scope already has the name (rotate it instead)
 *   PATCH  /secrets/v1/:id                 { description?, use?, provider?, rotateAfterDays?, disabled? } → SecretMeta
 *   PUT    /secrets/v1/:id/value           { value } → SecretMeta (version + 1): a rotation
 *   DELETE /secrets/v1/:id                 204
 *   POST   /secrets/v1/release             { projectId, rootId?, names?, purpose?, sessionId? } → { secrets: ReleasedSecret[], leaseSeconds, missing[] }
 *   POST   /secrets/v1/usage               { usage: SecretUsage[] } → 204: what a machine used, batched (the console's "last used")
 *
 * **Personal secrets** (scope `personal`, `ownerPrincipal`): one person's, kept at their home (the
 * organisation for an enterprise person, the hub on Teams, the private hub on Free), managed and
 * listed by that person only, released to their own machines — and to a node running their work
 * only when the secret says `follow`, the organisation allows it (`personalOnNodes`) and the node
 * asks for an open session of theirs (`sessionId`, `x-aioe-principal`).
 *
 * **Precedence** ({@link resolveSecrets}): a `locked` organisation or team secret wins over every
 * other of its name; otherwise the most specific shared scope (repository, project, team,
 * organisation); a personal secret fills a name nothing shared carries, and replaces a shared one
 * only when that one says `personalOverride: "allow"` and the policy's `personalSecrets` allows.
 *
 * **Change.** A home tells machines at once: a `secrets` event on each affected project's live
 * stream (`{ type: "secrets", change: "changed", scope, ids }`, and `change: "revoked"` with the
 * device when a machine is removed), so they release again instead of waiting for the lease.
 *
 * **Scope.** A secret belongs to the organisation, to one team, to one
 * project or to one repository of a project. For a project and repository,
 * the most specific secret of a name wins ({@link resolveSecrets}):
 * repository, then project, then a team granted to the project, then the
 * organisation. A team hub has no organisation above it: its hub-wide
 * secrets are scope `team`. A private hub's are the machine owner's, scope
 * `project` or `repository`, plus scope `team` for "every project on this
 * machine".
 *
 * **Release.** A signed-in workbench or node asks for what a project (and
 * one repository of it) may use. The home checks who is asking — the device,
 * and the person it works for: a desktop's user, or for a node the person
 * named in `x-aioe-principal` — against the project's membership, answers
 * the values with a lease, and writes one audit row per release
 * (`secret.release`: the names, versions and scopes, the device, the person,
 * the project and repository, the purpose — never a value). The machine
 * keeps what it was given in memory only and asks again before the lease
 * ends; a secret rotated, disabled or removed at the home is gone from the
 * machine at the next ask. `names` narrows the ask to what is needed (a home
 * may refuse an ask with no `names` from a node).
 *
 * **Use.** What a released secret may be used for is the secret's own
 * (`use`), decided where it is kept:
 *   - `tools`: handed to a tool server the workbench starts or reaches for
 *     an agent, by reference (`${secret:NAME}` in a repository's MCP
 *     configuration, a credential named by a published tool server). The
 *     workbench's gate holds it; the agent never sees it.
 *   - `provider`: a model provider's key (`provider` names which). The
 *     workbench's inference proxy holds it; the agent is given a stand-in.
 *   - `agent`: placed in the agent's own environment under its name (a
 *     package registry token a build needs). The only use an agent can read;
 *     a policy may forbid it ({@link secretsPolicySchema}).
 *
 * Every use on a machine is one audit row (`secret.use`: the name, version,
 * what used it, the session), and every released value is masked wherever
 * the workbench keeps text (audit, logs, transcripts, what it reports).
 *
 * Managing: organisation scope — the organisation's administrators; team
 * scope — the team's (or hub's) owners; project and repository scope — the
 * project's owners. Anyone on the project sees names and facts.
 *
 * Words for people: secret, kept by (your organisation, your team, this
 * project, this repository), used by (tools, a model provider, the agent's
 * environment), rotate, last used, released to.
 */
import { z } from "zod";
export const SECRETS_SCHEMA_VERSION = 1;
const id = z.string().min(1).max(200);
const by = z.object({ principal: z.string().max(200).optional(), actor: z.string().max(200).optional() });
/** A name an environment variable could carry: letters, digits, `_`, `.`, `-`, starting with a letter. */
export const SECRET_NAME = /^[A-Za-z][A-Za-z0-9_.-]{0,99}$/;
export const secretNameSchema = z.string().regex(SECRET_NAME, "a secret's name starts with a letter and holds letters, digits, _ . - (at most 100)");
export const SECRET_SCOPES = ["organisation", "team", "project", "repository", "personal"];
export const secretScopeSchema = z.enum(SECRET_SCOPES);
export const SECRET_USES = ["tools", "provider", "agent"];
export const secretUseSchema = z.enum(SECRET_USES);
/** The longest value kept: a PEM key fits, a file does not. */
export const SECRET_VALUE_MAX = 16 * 1024;
export const secretValueSchema = z.string().min(1).max(SECRET_VALUE_MAX);
const where = {
    scope: secretScopeSchema,
    /** Scope `team`: the team. Absent on a hub for "every project here". */
    teamId: id.optional(),
    /** Scope `project` and `repository`: the project. */
    projectId: id.optional(),
    /** Scope `repository`: the repository within the project. */
    rootId: id.optional(),
    /** Scope `personal`: the person whose it is (the home sets it to the caller; nobody else may). */
    ownerPrincipal: id.optional(),
};
/** A host name a `tools` secret may be sent to: `api.example.com` or `*.example.com`. */
export const secretHostSchema = z.string().regex(/^(\*\.)?[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/i, "a host name, or *.domain");
const facts = {
    name: secretNameSchema,
    description: z.string().max(500).optional(),
    use: z.array(secretUseSchema).min(1).max(3).default(["tools"]),
    /** For use `provider`: which provider's key this is (`anthropic`, `openai`, …). */
    provider: z.string().max(100).optional(),
    /** Remind its keepers when the value is older than this. */
    rotateAfterDays: z.number().int().min(1).max(3650).optional(),
    /** Scope `organisation` and `team`: wins over every other secret of its name (a project, repository or person cannot shadow it). */
    locked: z.boolean().optional(),
    /** Shared scopes: whether a person's own secret of the name may be used instead ("you may use your own"). Default `deny`. */
    personalOverride: z.enum(["allow", "deny"]).optional(),
    /** Scope `personal`: also released to a node running this person's work ("Follow me"). */
    follow: z.boolean().optional(),
    /** Use `tools`: the only hosts the value may be sent to (address, header or argument of a server for another host is refused). Absent: any. */
    allowedHosts: z.array(secretHostSchema).max(50).optional(),
};
export const secretDraftSchema = z.object({ ...where, ...facts, value: secretValueSchema });
export const secretPatchSchema = z
    .object({
    description: facts.description.nullable(),
    use: z.array(secretUseSchema).min(1).max(3),
    provider: facts.provider.nullable(),
    rotateAfterDays: facts.rotateAfterDays.nullable(),
    locked: z.boolean().nullable(),
    personalOverride: z.enum(["allow", "deny"]).nullable(),
    follow: z.boolean().nullable(),
    allowedHosts: z.array(secretHostSchema).max(50).nullable(),
    /** Kept, but released to nobody. */
    disabled: z.boolean(),
})
    .partial();
export const secretValuePutSchema = z.object({ value: secretValueSchema });
/** A grant of an existing secret to one project (and repository) until a date (item D; AIOE serves and mirrors it). Audit rows `secret.grant` and `secret.grant.expired`. */
export const secretGrantSchema = z.object({
    id,
    projectId: id,
    rootId: id.optional(),
    until: z.string().max(50),
    by: z.string().max(200).optional(),
    requestId: id.optional(),
});
/** Everything about a secret but its value. */
export const secretMetaSchema = z.looseObject({
    id,
    ...where,
    ...facts,
    /** 1, then +1 per rotation. */
    version: z.number().int().positive(),
    disabled: z.boolean().optional(),
    createdAt: z.string().max(50),
    createdBy: by.optional(),
    /** When the value last changed. */
    rotatedAt: z.string().max(50),
    rotatedBy: by.optional(),
    /** When it was last released to a machine. */
    lastReleasedAt: z.string().max(50).optional(),
    /** When a machine last reported using it, and who for (`POST /secrets/v1/usage`). */
    lastUsedAt: z.string().max(50).optional(),
    lastUsedBy: z.string().max(200).optional(),
    /** Uses reported, all machines together. */
    useCount: z.number().int().nonnegative().optional(),
    /**
     * Slice 1 (Infisical audit, item D): a team- or project-scoped secret also reaches these projects (and repositories)
     * until `until`. `resolveSecrets` treats a live grant as project (or repository) scope of the target, at that scope's
     * place in precedence; an expired grant is ignored. AIOE serves and mirrors this shape.
     */
    grants: z.array(secretGrantSchema).max(50).optional(),
    /** Item G: the version this value was restored from, when the current one is a restore. AIOE serves it. */
    restoredFrom: z.number().int().positive().optional(),
});
export const secretReleaseRequestSchema = z.object({
    projectId: id,
    rootId: id.optional(),
    /** Only these, when given. */
    names: z.array(secretNameSchema).max(200).optional(),
    /** Why, for the audit row: "session start", "tool server tracker", "refresh". */
    purpose: z.string().max(200).optional(),
    /** A node's open session for the person in `x-aioe-principal`: what lets a personal secret that follows them be released to it. */
    sessionId: id.optional(),
    /** The machine understands scope `personal`; without it none is released (a machine from before personal secrets would refuse the answer). */
    personal: z.boolean().optional(),
});
export const releasedSecretSchema = z.object({
    id,
    name: secretNameSchema,
    value: secretValueSchema,
    version: z.number().int().positive(),
    scope: secretScopeSchema,
    use: z.array(secretUseSchema).min(1),
    provider: z.string().max(100).optional(),
    /** Carried so the machine enforces it: a `tools` value goes to these hosts only. */
    allowedHosts: z.array(secretHostSchema).optional(),
    /** Carried so the machine refuses a personal or machine value of this name (an organisation's provider key, say). */
    locked: z.boolean().optional(),
});
export const secretReleaseResponseSchema = z.object({
    secrets: z.array(releasedSecretSchema),
    /** Ask again before this many seconds pass; what is held is dropped when they do. */
    leaseSeconds: z.number().int().min(30).max(24 * 3600),
    /** Names asked for that no secret in reach carries. */
    missing: z.array(z.string()).default([]),
});
/** One secret's use on one machine since the last report (`POST /secrets/v1/usage`). */
export const secretUsageSchema = z.object({
    id,
    version: z.number().int().positive(),
    use: secretUseSchema,
    count: z.number().int().positive().max(1_000_000),
    firstAt: z.string().max(50),
    lastAt: z.string().max(50),
    sessionIds: z.array(z.string().max(200)).max(50).default([]),
});
export const secretUsageReportSchema = z.object({ usage: z.array(secretUsageSchema).min(1).max(500) });
/** How long a machine may hold what it was given, by default. */
export const SECRET_LEASE_SECONDS = 15 * 60;
// ── Which secret a name means ──────────────────────────────────────────
const SCOPE_RANK = { repository: 0, project: 1, team: 2, organisation: 3, personal: 4 };
/** The live grant of a secret for this reach (not past its `until`, for this project and, when it names one, this repository), if any. */
export function liveGrant(s, at) {
    const now = at.now ?? Date.now();
    return s.grants?.find((g) => g.projectId === at.projectId && (g.rootId === undefined || g.rootId === at.rootId) && Date.parse(g.until) > now);
}
/** Whether a secret is in reach of a project (and one repository of it). `teamIds`: the teams granted to the project; a `team` secret with no team is hub-wide. */
export function secretApplies(s, at) {
    // A live grant (item D) reaches a team or project secret the project is otherwise outside of.
    if ((s.scope === "team" || s.scope === "project" || s.scope === "repository") && liveGrant(s, at))
        return true;
    switch (s.scope) {
        case "personal":
            return at.personalSecrets !== "deny" && at.principal !== undefined && s.ownerPrincipal === at.principal;
        case "organisation":
            return true;
        case "team":
            return s.teamId === undefined || (at.teamIds ?? []).includes(s.teamId);
        case "project":
            return s.projectId === at.projectId;
        case "repository":
            return s.projectId === at.projectId && at.rootId !== undefined && s.rootId === at.rootId;
    }
}
/**
 * The secret each name means for a project and repository, among those in reach and not disabled:
 *
 *   1. a `locked` organisation or team secret wins over everything of its name;
 *   2. otherwise the most specific shared scope — repository, project, team, organisation (two
 *      teams' secrets of one name: the first team in `teamIds` wins, a hub-wide one last);
 *   3. the person's own (`personal`) fills a name nothing shared carries, and replaces a shared
 *      one only when that one says `personalOverride: "allow"` (never a locked one);
 *   4. `personalSecrets: "deny"` drops personal secrets altogether.
 */
export function resolveSecrets(all, at) {
    // A secret reached only through a grant ranks as the project's (or the repository's, when the grant names one).
    const reachedByGrant = (s) => !!liveGrant(s, at) && !(s.scope === "team" ? s.teamId === undefined || (at.teamIds ?? []).includes(s.teamId) : s.projectId === at.projectId && (s.scope !== "repository" || s.rootId === at.rootId));
    const rankOf = (s) => (reachedByGrant(s) ? (liveGrant(s, at)?.rootId !== undefined ? SCOPE_RANK.repository : SCOPE_RANK.project) : SCOPE_RANK[s.scope]);
    const teamRank = (s) => (s.scope !== "team" ? 0 : s.teamId === undefined ? Number.MAX_SAFE_INTEGER : Math.max(0, (at.teamIds ?? []).indexOf(s.teamId)));
    const isLocked = (s) => s.locked === true && (s.scope === "organisation" || s.scope === "team");
    const shared = new Map();
    const personal = new Map();
    for (const s of all) {
        if (s.disabled || !secretApplies(s, at))
            continue;
        if (s.scope === "personal") {
            if (!personal.has(s.name))
                personal.set(s.name, s);
            continue;
        }
        const held = shared.get(s.name);
        const better = !held || (isLocked(s) && !isLocked(held)) || (isLocked(s) === isLocked(held) && (rankOf(s) < rankOf(held) || (rankOf(s) === rankOf(held) && s.scope === held.scope && teamRank(s) < teamRank(held))));
        if (better)
            shared.set(s.name, s);
    }
    const out = new Map(shared);
    for (const [name, mine] of personal) {
        const theirs = shared.get(name);
        if (!theirs || (theirs.personalOverride === "allow" && !isLocked(theirs)))
            out.set(name, mine);
    }
    return [...out.values()].sort((a, b) => a.name.localeCompare(b.name));
}
/** Whether `host` is one a secret with `allowedHosts` may be sent to (`*.example.com` matches any subdomain, not the domain itself). */
export function secretHostAllowed(allowed, host) {
    if (!allowed || allowed.length === 0)
        return true;
    const h = host.toLowerCase().replace(/\.$/, "");
    return allowed.some((a) => {
        const p = a.toLowerCase();
        return p.startsWith("*.") ? h.endsWith(p.slice(1)) && h.length > p.length - 1 : h === p;
    });
}
/** Whether its value is older than its keepers asked to be reminded at. */
export function secretIsStale(s, now) {
    if (!s.rotateAfterDays)
        return false;
    const at = Date.parse(s.rotatedAt);
    return Number.isFinite(at) && now - at > s.rotateAfterDays * 24 * 3600 * 1000;
}
// ── References: how a configuration names a secret without holding it ──
/** `${secret:NAME}` in a tool server's command arguments, environment, address or headers. */
export const SECRET_REFERENCE = /\$\{secret:([A-Za-z][A-Za-z0-9_.-]{0,99})\}/g;
/** The secrets a text names, once each, in order. */
export function secretReferences(text) {
    const names = [];
    for (const m of text.matchAll(SECRET_REFERENCE))
        if (!names.includes(m[1]))
            names.push(m[1]);
    return names;
}
/** The text with every reference replaced by its value; `missing` names the ones `lookup` did not have (left in place). */
export function expandSecretReferences(text, lookup) {
    const used = [];
    const missing = [];
    const out = text.replace(SECRET_REFERENCE, (whole, name) => {
        const value = lookup(name);
        if (value === undefined) {
            if (!missing.includes(name))
                missing.push(name);
            return whole;
        }
        if (!used.includes(name))
            used.push(name);
        return value;
    });
    return { text: out, used, missing };
}
// ── The policy's say ───────────────────────────────────────────────────
/** An owner in `accountOwners`: `owner`, or `host/owner` for a GitHub Enterprise Server. */
function accountOwnerSchema() {
    return z.string().regex(/^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+\/)?[A-Za-z0-9_][A-Za-z0-9_.-]{0,99}$/i, "an owner (mojo-up), or host/owner for a GitHub Enterprise Server");
}
/**
 * The `secrets` clause of the workspace policy. Left out, nothing is
 * restricted beyond what each secret's own `use` says.
 */
export const secretsPolicySchema = z.object({
    /** May a secret be placed in an agent's own environment (use `agent`)? `deny` withholds every one, whatever the secret says. */
    agentEnvironment: z.enum(["allow", "deny"]).default("allow"),
    /** May a repository's tool servers read this machine's environment variables (`${env:NAME}`)? `deny` leaves only `${secret:NAME}`. */
    machineEnvironment: z.enum(["allow", "deny"]).default("allow"),
    /** May secrets a person keeps on their own machine be used in this project? `deny` leaves only the home's. */
    personalSecrets: z.enum(["allow", "deny"]).default("allow"),
    /** May a personal secret that says "Follow me" be released to a node running that person's work? */
    personalOnNodes: z.enum(["allow", "deny"]).default("deny"),
    /** Refuse to keep shared secrets on a machine with no operating-system key store (a Linux box without a keyring). */
    requireKeyStore: z.boolean().default(false),
    /**
     * What an agent's process inherits from the host's environment. `keep` (the default): as today. `clean`:
     * nothing named like a credential (`*TOKEN*`, `*SECRET*`, `*PASSWORD*`, `*API_KEY*`, `*PRIVATE_KEY*`,
     * `*ACCESS_KEY*`), nor `GITHUB_TOKEN`, `GH_TOKEN` or `SSH_AUTH_SOCK`; what it needs comes as a released
     * secret. `allow` names variables kept even when clean.
     */
    inheritedCredentials: z
        .object({
        mode: z.enum(["keep", "clean"]).default("keep"),
        allow: z.array(z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,99}$/)).max(100).optional(),
    })
        .optional(),
    /** Decoy keys: each agent run is given one credential that looks real and is valid nowhere; its appearance in anything leaving the host stops the run. Absent: on. */
    decoyKeys: z.enum(["on", "off"]).optional(),
    /**
     * Whose repositories a machine's own GitHub or Azure DevOps sign-in may be handed to (D177). Per
     * provider, the owners allowed: a GitHub user or organisation (`mojo-up`, on github.com; `host/owner`
     * for a GitHub Enterprise Server, `github.acme.com/platform`), an Azure DevOps organisation
     * (`mojoup`). A repository with no remote under one of them gets no token. A provider left out, or
     * the whole field: any repository with a remote on that provider.
     */
    accountOwners: z
        .object({
        github: z.array(accountOwnerSchema()).max(200).optional(),
        azureDevOps: z.array(accountOwnerSchema()).max(200).optional(),
    })
        .optional(),
    /**
     * Scanning an agent's work for secrets before it lands (item C, src/git/secretScan.ts). `mode`: `block` (the default)
     * blocks the review on a secret's exact value or a decoy key and warns on credential patterns and random-looking strings;
     * `warn` only warns; `off` does not scan. `allowedFindings`: a pattern accepted in files that match (`file` is a glob,
     * `pattern` a pattern key such as `token` or `entropy`, or `*`), each with its reason. AIOE mirrors this from the manifest.
     */
    scan: z
        .object({
        mode: z.enum(["off", "warn", "block"]).default("block"),
        allowedFindings: z.array(z.object({ file: z.string().min(1).max(300), pattern: z.string().min(1).max(100), reason: z.string().min(1).max(500) })).max(200).optional(),
    })
        .optional(),
    /** Item G (AIOE's; the workbench only shows it): how long the previous value is kept after a rotation. 0 drops it at once. Default 7. */
    keepPreviousDays: z.number().int().min(0).max(90).optional(),
    /** Locked-secret changes: `direct` (default) lets any keeper change it; `second-keeper` holds each change for another keeper. AIOE's; schema only here. */
    lockedChanges: z.enum(["direct", "second-keeper"]).optional(),
});
export const SECRET_SCOPE_TEXT = { organisation: "Your organisation", team: "Your team", project: "This project", repository: "This repository", personal: "Yours" };
export const SECRET_USE_TEXT = { tools: "Tools", provider: "A model provider", agent: "The agent's environment" };
// ── Slice 1 of the Infisical audit: asking for a secret, and undoing a rotation ─────────────────────
// docs/research/2026-11-infisical-audit.md items D and G; docs/plans/2026-11-secrets-slice-1.md.
// AIOE serves and mirrors every shape below (POST /secrets/v1/requests, GET /secrets/v1/requests?projectId=&state=,
// POST /secrets/v1/requests/:id/decide); the workbench's own home or hub serves the same record locally first.
export const SECRET_REQUEST_STATES = ["open", "granted", "added", "refused", "expired"];
export const secretRequestStateSchema = z.enum(SECRET_REQUEST_STATES);
/** Asking for a secret a project names and the person cannot reach. Deduplicated per project and name while open. */
export const secretRequestCreateSchema = z.object({
    projectId: id,
    rootId: id.optional(),
    name: secretNameSchema,
    use: secretUseSchema.optional(),
    /** What the person typed: why they need it. */
    why: z.string().max(500).optional(),
    sessionId: id.optional(),
});
/** A request for a secret, with who asked and what became of it. Never a value. */
export const secretRequestSchema = z.object({
    id,
    ...secretRequestCreateSchema.shape,
    /** Who asks (principal) and, for display, their name. */
    askedBy: z.string().max(200),
    askedByName: z.string().max(200).optional(),
    /** "for 7 days": the longest the person asks for, when they said. */
    days: z.number().int().min(1).max(90).optional(),
    state: secretRequestStateSchema,
    askedAt: z.string().max(50),
    /** An open request lapses at this time (default 14 days after it was made) and becomes `expired`. */
    lapsesAt: z.string().max(50).optional(),
    decidedAt: z.string().max(50).optional(),
    decidedBy: z.string().max(200).optional(),
    /** `granted` or `added`: the secret that answered it. */
    secretId: id.optional(),
    /** `granted`: until when. */
    grantUntil: z.string().max(50).optional(),
    /** `refused`: the keeper's reason. */
    reason: z.string().max(500).optional(),
});
export const secretRequestDecisionSchema = z.discriminatedUnion("answer", [
    z.object({ answer: z.literal("grant"), secretId: id, days: z.number().int().min(1).max(90) }),
    z.object({ answer: z.literal("added"), secretId: id }),
    z.object({ answer: z.literal("refuse"), reason: z.string().max(500).optional() }),
]);
/** How long an unanswered request stays open (days). */
export const SECRET_REQUEST_LAPSE_DAYS = 14;
/** One version of a secret in its history (item G). */
export const secretVersionSchema = z.object({
    version: z.number().int().positive(),
    at: z.string().max(50),
    by: z.string().max(200).optional(),
    current: z.literal(true).optional(),
    restorable: z.boolean(),
    until: z.string().max(50).optional(),
    restoredFrom: z.number().int().positive().optional(),
});
export const secretVersionsAnswerSchema = z.object({ versions: z.array(secretVersionSchema), keepDays: z.number() });
/** A change to a locked organisation secret held for a second keeper (`secrets.lockedChanges: second-keeper`). Schema only in the workbench. */
export const secretChangeSchema = z.object({
    id,
    secretId: id,
    kind: z.enum(["update", "rotate", "remove", "restore"]),
    facts: z.record(z.string(), z.unknown()).optional(),
    restoreVersion: z.number().int().positive().optional(),
    by: z.string().max(200),
    at: z.string().max(50),
    state: z.enum(["pending", "approved", "refused", "withdrawn"]),
    decidedBy: z.string().max(200).optional(),
    decidedAt: z.string().max(50).optional(),
});
//# sourceMappingURL=secrets.js.map